// Count lines: one per product, counted in one of its units.
// The approval moves stock by approved (in base units) less the site's stock when the count was sent.
import { type ProductUnit, unitsOfProduct } from '../../components/purchasing/units';
import type { CountDocumentLine, CountLinesPayload, CountUnit } from '../../services/count.service';
import { baseUnitsPerUnit, parseNumber, qtyText } from '../Order/orderLineRules';

export interface CountLine {
  /** The product id: the API keeps one line per product. */
  key: string;
  /** The saved line; null for a product added on screen. */
  id: string | null;
  productId: string;
  name: string;
  sku: string;
  group: string;
  /** As the API sends them, for the base-unit ratio the API uses. */
  rawUnits: CountUnit[];
  units: ProductUnit[];
  productUOMId: string;
  /** As typed; empty = not counted. */
  counted: string;
  approved: string;
  /** Base units; the site's stock when the count was sent. */
  expectedQty: number | null;
  /** Per line unit, as saved; 0 for a line not saved yet. */
  cost: number;
  /** Base units at the site now. */
  onHand: number;
}

const round2 = (value: number) => Math.round(value * 100) / 100;
const round4 = (value: number) => Math.round(value * 10000) / 10000;
const text = (value: unknown) => (value === null || value === undefined ? '' : qtyText(value));

export const lineOfDocument = (line: CountDocumentLine): CountLine => {
  const rawUnits = Array.isArray(line.units) ? line.units : [];
  const units = unitsOfProduct({ productUOMs: rawUnits });
  return {
    key: String(line.productId),
    id: line.id,
    productId: String(line.productId),
    name: line.productName ?? '',
    sku: line.sku ?? '',
    group: line.group ?? '',
    rawUnits,
    units,
    productUOMId: line.productUOMId || units.find((unit) => unit.isBase)?.id || '',
    counted: text(line.countedQty),
    approved: text(line.approvedQty),
    expectedQty: line.expectedQty === null || line.expectedQty === undefined ? null : Number(line.expectedQty),
    cost: Number(line.cost) || 0,
    onHand: Number(line.onHand) || 0,
  };
};

/** A product picked from the site's list, not counted yet. */
export const lineOfPick = (pick: any, unitId: string): CountLine => {
  const rawUnits: CountUnit[] = (pick?.productUOMs ?? []).filter((unit: any) => unit?.id);
  return {
    key: String(pick?.id ?? ''),
    id: null,
    productId: String(pick?.id ?? ''),
    name: pick?.productName ?? '',
    sku: pick?.sku ?? '',
    group: pick?.productGroup?.groupName ?? '',
    rawUnits,
    units: unitsOfProduct({ productUOMs: rawUnits }),
    productUOMId: unitId,
    counted: '',
    approved: '',
    expectedQty: null,
    cost: 0,
    onHand: Number(pick?.currentStock) || 0,
  };
};

export const unitName = (line: Pick<CountLine, 'units' | 'productUOMId'>): string => line.units.find((unit) => unit.id === line.productUOMId)?.name ?? '';
export const baseName = (line: Pick<CountLine, 'units'>): string => line.units.find((unit) => unit.isBase)?.name ?? '';

/** Base units in one unit of the line, as the API works it out. */
export const basePer = (line: Pick<CountLine, 'rawUnits' | 'productUOMId'>): number => baseUnitsPerUnit(line.rawUnits, line.productUOMId);

export interface QtyCheck {
  /** Null when left empty: the product is not counted. */
  qty: number | null;
  error: string;
}

/** A counted or approved figure: empty is allowed (not counted); 0 is a count. */
export const checkQty = (typed: string): QtyCheck => {
  const n = parseNumber(typed);
  if (n === null) return { qty: null, error: '' };
  if (Number.isNaN(n)) return { qty: null, error: 'Enter a number' };
  if (n < 0) return { qty: null, error: "Can't be negative" };
  return { qty: round4(n), error: '' };
};

/** Base units the approval moves for the line; null when it is not counted or nothing was sent with it. */
export const varianceOf = (line: Pick<CountLine, 'rawUnits' | 'productUOMId' | 'expectedQty'>, typed: string): number | null => {
  const { qty, error } = checkQty(typed);
  if (qty === null || error || line.expectedQty === null) return null;
  return round4(qty * basePer(line) - line.expectedQty);
};

/** The stock value a difference moves, at the line's cost per base unit. */
export const varianceValue = (line: Pick<CountLine, 'rawUnits' | 'productUOMId' | 'cost'>, variance: number | null): number | null =>
  variance === null ? null : round2((variance * line.cost) / basePer(line));

/** Stock up, stock down and the net, over every line with a difference. */
export const valueTotals = (lines: CountLine[], typedOf: (line: CountLine) => string) =>
  lines.reduce(
    (sum, line) => {
      const value = varianceValue(line, varianceOf(line, typedOf(line))) ?? 0;
      if (value > 0) sum.up = round2(sum.up + value);
      if (value < 0) sum.down = round2(sum.down + value);
      sum.net = round2(sum.up + sum.down);
      return sum;
    },
    { up: 0, down: 0, net: 0 },
  );

const same = (a: string, b: string) => {
  const x = checkQty(a);
  const y = checkQty(b);
  return x.error || y.error ? a.trim() === b.trim() : x.qty === y.qty;
};

export type CountChange = { kind: 'added' | 'removed' | 'edited'; key: string };

/** While counting: added, removed, recounted or re-united lines, by product. */
export const countingChanges = (lines: CountLine[], saved: CountLine[]): CountChange[] => {
  const before = new Map(saved.map((line) => [line.key, line]));
  const now = new Set(lines.map((line) => line.key));
  const out: CountChange[] = [];
  lines.forEach((line) => {
    const old = before.get(line.key);
    if (!old || !line.id) out.push({ kind: 'added', key: line.key });
    else if (old.productUOMId !== line.productUOMId || !same(old.counted, line.counted)) out.push({ kind: 'edited', key: line.key });
  });
  saved.forEach((line) => {
    if (!now.has(line.key)) out.push({ kind: 'removed', key: line.key });
  });
  return out;
};

/** While waiting for approval: lines whose approved figure changed. */
export const approvalChanges = (lines: CountLine[], saved: CountLine[]): CountChange[] => {
  const before = new Map(saved.map((line) => [line.key, line]));
  return lines.filter((line) => !same(before.get(line.key)?.approved ?? '', line.approved)).map((line) => ({ kind: 'edited' as const, key: line.key }));
};

/** What PUT /:id/lines takes for the changes; only call it when no line has an error. */
export const linesPayload = (lines: CountLine[], saved: CountLine[], mode: 'counting' | 'approval'): CountLinesPayload => {
  const byKey = new Map(lines.map((line) => [line.key, line]));
  if (mode === 'approval') {
    return {
      lines: approvalChanges(lines, saved).map(({ key }) => {
        const line = byKey.get(key) as CountLine;
        return { id: line.id as string, productId: line.productId, approvedQty: checkQty(line.approved).qty };
      }),
    };
  }
  const changes = countingChanges(lines, saved);
  return {
    lines: changes
      .filter((change) => change.kind !== 'removed')
      .map(({ key }) => {
        const line = byKey.get(key) as CountLine;
        return { ...(line.id ? { id: line.id } : {}), productId: line.productId, productUOMId: line.productUOMId, countedQty: checkQty(line.counted).qty };
      }),
    removedIds: changes
      .filter((change) => change.kind === 'removed')
      .map(({ key }) => saved.find((line) => line.key === key)?.id)
      .filter((id): id is string => !!id),
  };
};
